import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@kidshome/ui';
import {
  resolveErrorMessage,
  type TodayHabitResponse,
  type TodayHabitsResponse,
} from '@kidshome/api-contract';
import { EmptyState, habitGlyph, FlameIcon, SproutIcon } from '@/shared/ui';
import { ConceptIcon } from '@/shared/ui/ConceptIcon';
import { habitArt } from '@/assets/icons';
import { tDynamic } from '@/shared/lib/i18n';
import { useHouseholdContext } from '@/shared/lib/household';
import { scheduleSummary } from '@/shared/lib/schedule-model';
import { useCheckHabitAction } from '@/features/check-habit';
import { useBondReaction } from '@/widgets/bond-reaction';
import './habit-board.css';

/**
 * The ONE kid habit card, used everywhere a child sees their habits — the Home "habits today" section
 * (via the `<HabitBoard today=…/>` wrapper) and the dedicated `/habits` focus view both mount this
 * exact list, so a habit looks and behaves identically on either surface (the mockup's `.habit-card`:
 * glyph · name · cadence · kind streak, plus the big one-tap check).
 *
 * The check is optimistic + offline-safe via `useCheckHabitAction` (the PWA outbox replays the
 * idempotent write). Checking a habit floats the REAL, transient "+6 до зв'язку" bond reaction — fired
 * only once the SERVER confirms the check (never at tap time, since an offline/rejected write rolls the
 * optimistic check back). A done habit gets a soft "done for today" treatment — encouraging, never a
 * scoreboard. A habit NOT scheduled today (`dueToday: false`, and not already done) renders a calm
 * "not today" chip instead of the check — the API rejects non-due check-ins, so the list never teases a
 * tap it would have to roll back.
 *
 * The board acts on TODAY only (ADR-0053): a child never back-dates a missed day — repairing the past is
 * a grown-up's call on the habit detail screen. The kid can still SEE past days (the detail heatmap).
 *
 * Empty state is kind: a child sees "no habits yet — check back soon"; a guardian (rare on this kid
 * surface) gets a CTA into habit management. The `canCheck` gate hides the affordance for a viewer
 * without `habit:check:own` (the API self-scopes + enforces regardless).
 */
export function HabitList({
  habits,
  canCheck,
  canManage,
}: {
  habits: readonly TodayHabitResponse[];
  /** Whether the viewer may check their own habits (gates the tap affordance). */
  canCheck: boolean;
  /** Whether the viewer can manage habits (shows an "add a habit" CTA on the empty state). */
  canManage?: boolean;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const { check, uncheck, isHabitPending, takeSettledError, error } = useCheckHabitAction();
  // The REAL, transient "+6 до зв'язку" reaction — fired only once the SERVER confirms the check, never
  // at tap time. The optimistic check can roll back on an offline/rejected write (use-check-habit
  // onError restores the row); firing synchronously would float a +6 reward for a check that never
  // landed. So we track each habit tapped-to-check and fire from the settle: when a tracked id leaves
  // the pending set with no error it confirmed → fire; if an error surfaced it rolled back → drop it.
  // The fire decision reads `takeSettledError(id)` — a PER-HABIT outcome — rather than the hook's shared
  // `error`: `check`/`uncheck` for every habit share one check-in mutation observer, so if a second habit
  // is tapped before the first settles, the shared `error` can only ever reflect the most-recently-
  // started call (TanStack Query detaches the observer from the earlier in-flight mutation), which would
  // misattribute one habit's outcome to another's settle.
  const { reaction, fire } = useBondReaction();
  const awaitingCheck = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (awaitingCheck.current.size === 0) return;
    for (const id of [...awaitingCheck.current]) {
      if (isHabitPending(id)) continue; // still in flight — wait for settle
      awaitingCheck.current.delete(id);
      if (takeSettledError(id) == null) fire('habit'); // confirmed by the server → the +6 is real
    }
  }, [isHabitPending, takeSettledError, fire]);

  if (habits.length === 0) {
    return (
      <EmptyState
        className="habit-empty"
        art="empty.habits"
        glyph="🌱"
        message={canManage ? t('habits.emptyGuardian') : t('habits.emptyKid')}
        action={
          canManage ? (
            <Button asChild variant="secondary" size="sm">
              <Link to="/admin/habits">{t('habits.emptyGuardianCta')}</Link>
            </Button>
          ) : undefined
        }
      />
    );
  }

  const errorMessage = (() => {
    if (error == null) return null;
    const m = resolveErrorMessage(error);
    return tDynamic(t, m.key, m.params);
  })();

  return (
    <div className="habit-list">
      {/* The bond reaction floats here only when a habit is checked off (+6) — a transient event that
          fades; the hero's ring then advances live on /hero. */}
      {reaction && (
        <div
          aria-hidden="true"
          style={{
            // Absolutely positioned (OUT of the list's flex flow) so the transient "+N" float never takes a
            // slot: a sticky/flow element would also contribute the list's 14px row-gap and shove every card
            // down while it's shown, then collapse — flashing an empty gap on each check. Anchored to the
            // relatively-positioned `.habit-list`, it just overlays the top of the first card and fades.
            position: 'absolute',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            top: 8,
            zIndex: 5,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {reaction}
        </div>
      )}
      {errorMessage && (
        <p role="alert" className="habit-error">
          {errorMessage}
        </p>
      )}
      {habits.map((habit) => (
        <HabitCard
          key={habit.id}
          habit={habit}
          canCheck={canCheck}
          canManage={canManage ?? false}
          pending={isHabitPending(habit.id)}
          onToggle={() => {
            if (habit.doneToday) {
              uncheck(habit.id);
            } else {
              check(habit.id);
              // Defer the +6 reaction to the settle (above) so it only floats on a confirmed check.
              awaitingCheck.current.add(habit.id);
            }
          }}
        />
      ))}
    </div>
  );
}

function HabitCard({
  habit,
  canCheck,
  canManage,
  pending,
  onToggle,
}: {
  habit: TodayHabitResponse;
  canCheck: boolean;
  /** Whether the viewer may manage habits — a guardian can still check a guardian-only habit. */
  canManage: boolean;
  pending: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const { t } = useTranslation('family');
  // The shared `schedule.*` cadence vocabulary lives in the admin catalog (bundled on every path).
  const { t: tSchedule } = useTranslation('admin');
  const { locale } = useHouseholdContext();

  const active = habit.currentStreak > 0;
  // The "shown up N days" line IS app copy (translated + pluralized); a fresh start gets a warm nudge.
  const streakLabel = active
    ? t('habits.shownUp', { count: habit.currentStreak })
    : t('habits.shownUpStart');
  // Best is only worth showing once it exceeds the current run (a record to chase, never a "lost" number).
  const showBest = habit.bestStreak > habit.currentStreak && habit.bestStreak > 0;

  return (
    <div className={`card habit-card${habit.doneToday ? ' is-done' : ''}`}>
      <div className="row">
        {/* The glyph + name + cadence + streak open the detail. The check stays a SIBLING. */}
        <Link
          to={`/habits/${habit.id}`}
          aria-label={t('habits.openDetail', { name: habit.name })}
          className="habit-open"
        >
          <span className="habit-glyph" aria-hidden="true">
            <ConceptIcon art={habitArt(habit.icon)} glyph={habitGlyph(habit.icon)} size={28} />
          </span>
          <div className="habit-text">
            {/* Habit name is raw user content — rendered verbatim, never translated. */}
            <div className="habit-name">{habit.name}</div>
            <div className="habit-cadence">
              {scheduleSummary(habit.schedule, undefined, tSchedule, locale)}
            </div>
            <div className={`habit-streak${active ? '' : ' is-fresh'}`}>
              <span className="fire" aria-hidden="true">
                {active ? (
                  <FlameIcon className="text-[#ee8a33]" />
                ) : (
                  <SproutIcon className="text-success" />
                )}
              </span>
              <span>{streakLabel}</span>
              {showBest && (
                <span className="best">· {t('habits.best', { count: habit.bestStreak })}</span>
              )}
            </div>
          </div>
        </Link>

        {!habit.doneToday && !habit.dueToday ? (
          // Not scheduled today (e.g. a Mon/Wed/Fri habit on a Tuesday) AND not already done: the API
          // rejects a check-in on a non-due day, so never offer the tap — a calm "not today" chip.
          // (A habit that is doneToday keeps its uncheck control even if a guardian later trimmed the
          // schedule to exclude today — `doneToday` is the dominant state, so the green "done" card
          // never strands the kid with only a "not today" chip and no way to undo.)
          <span className="not-today">{t('habits.notToday')}</span>
        ) : habit.guardianCheckOnly && !canManage ? (
          // Guardian-only habit: the kid sees it (and its streak) but only a grown-up may check it in —
          // the API rejects a child's check-in, so never offer the tap. A done one shows the green check
          // statically (informative); an undone one a calm "a grown-up checks this" chip.
          habit.doneToday ? (
            <span
              className="check-btn checked is-static"
              role="img"
              aria-label={t('habits.guardianChecked')}
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          ) : (
            <span className="guardian-only">{t('habits.guardianOnly')}</span>
          )
        ) : (
          canCheck && (
            <button
              type="button"
              onClick={onToggle}
              disabled={pending}
              aria-pressed={habit.doneToday}
              aria-label={
                habit.doneToday
                  ? t('habits.uncheck', { name: habit.name })
                  : t('habits.check', { name: habit.name })
              }
              className={`check-btn${habit.doneToday ? ' checked' : ''}`}
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={habit.doneToday ? 1 : 0.5}
                />
              </svg>
            </button>
          )
        )}
      </div>
    </div>
  );
}

/**
 * Thin back-compat wrapper for callers that hold the whole `GET /habits/today` response (the Home
 * screen + the a11y harness). It simply mounts the shared `<HabitList/>` over `today.habits`, so Home
 * and the `/habits` focus view render the exact same card.
 */
export function HabitBoard({
  today,
  canCheck,
  canManage,
}: {
  today: TodayHabitsResponse;
  /** Whether the viewer may check their own habits (gates the tap affordance). */
  canCheck: boolean;
  /** Whether the viewer can manage habits (shows an "add a habit" CTA on the empty state). */
  canManage?: boolean;
}): React.ReactElement {
  return <HabitList habits={today.habits} canCheck={canCheck} canManage={canManage} />;
}
